import { apiRequest } from '@/lib/api/client';
import type {
  TeacherAssignment,
  TeacherAttendanceRecord,
  TeacherAttestationEntry,
  TeacherDiscipline,
  TeacherGradeRecord,
  TeacherGroup,
  TeacherLessonWeek,
  TeacherMeeting,
  TeacherPersonalFile,
  TeacherScientificProject,
  TeacherStudent,
  TeacherStudentRequest,
  TeacherSubmission,
  TeacherWorkspaceSummary,
} from './types';

const apiClient = {
  get: <T>(path: string) => apiRequest<T>(path, { method: 'GET', channel: 'platform' }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body, channel: 'platform' }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body, channel: 'platform' }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE', channel: 'platform' }),
};

export const teacherApi = {
  enroll: (discipline_code: string, group_code: string, academia_id: string) => apiClient.post<TeacherStudent>('/teacher/enrollments', { discipline_code, group_code, academia_id }),
  configureCalendar: (code: string, weeks: number, rk1_week: number, rk2_week: number, grading_weights?: Record<string, number> | null) => apiClient.put(`/teacher/disciplines/${encodeURIComponent(code)}/calendar`, { weeks, rk1_week, rk2_week, grading_weights }),
  editAssignment: (id: string, payload: Partial<TeacherAssignment>) => apiClient.put<TeacherAssignment>(`/teacher/assignments/${id}`, payload),
  getSummary: (): Promise<TeacherWorkspaceSummary> =>
    apiClient.get<TeacherWorkspaceSummary>('/teacher/summary'),

  getDisciplines: (): Promise<TeacherDiscipline[]> =>
    apiClient.get<TeacherDiscipline[]>('/teacher/disciplines'),

  createDiscipline: (payload: {
    code: string;
    title: string;
    credits?: number;
    degree_level?: string;
  }): Promise<TeacherDiscipline> =>
    apiClient.post<TeacherDiscipline>('/teacher/disciplines', payload),

  getGroups: (disciplineCode: string): Promise<TeacherGroup[]> =>
    apiClient.get<TeacherGroup[]>(`/teacher/groups?discipline_code=${encodeURIComponent(disciplineCode)}`),

  getStudents: (disciplineCode: string, groupCode: string): Promise<TeacherStudent[]> =>
    apiClient.get<TeacherStudent[]>(
      `/teacher/students?discipline_code=${encodeURIComponent(disciplineCode)}&group_code=${encodeURIComponent(groupCode)}`
    ),

  getWeeks: (disciplineCode: string): Promise<TeacherLessonWeek[]> =>
    apiClient.get<TeacherLessonWeek[]>(`/teacher/weeks?discipline_code=${encodeURIComponent(disciplineCode)}`),

  updateWeek: (
    payload: Partial<TeacherLessonWeek> & { discipline_code: string; week_number: number }
  ): Promise<TeacherLessonWeek> =>
    apiClient.put<TeacherLessonWeek>('/teacher/weeks', payload),

  getAssignments: (disciplineCode: string, groupCode?: string): Promise<TeacherAssignment[]> => {
    const query = groupCode
      ? `/teacher/assignments?discipline_code=${encodeURIComponent(disciplineCode)}&group_code=${encodeURIComponent(groupCode)}`
      : `/teacher/assignments?discipline_code=${encodeURIComponent(disciplineCode)}`;
    return apiClient.get<TeacherAssignment[]>(query);
  },

  createAssignment: (payload: Partial<TeacherAssignment>): Promise<TeacherAssignment> =>
    apiClient.post<TeacherAssignment>('/teacher/assignments', payload),

  getSubmissions: (assignmentId?: string, disciplineCode?: string, groupCode?: string): Promise<TeacherSubmission[]> => {
    const query = assignmentId
      ? `/teacher/submissions?assignment_id=${encodeURIComponent(assignmentId)}`
      : '/teacher/submissions';
    return apiClient.get<TeacherSubmission[]>(`${query}${query.includes("?") ? "&" : "?"}discipline_code=${encodeURIComponent(disciplineCode || "")}&group_code=${encodeURIComponent(groupCode || "")}`);
  },

  gradeSubmission: (
    submissionId: string,
    payload: { score: number | null; feedback?: string; status?: string }
  ): Promise<TeacherSubmission> =>
    apiClient.put<TeacherSubmission>(`/teacher/submissions/${submissionId}/grade`, payload),

  getGradebook: (disciplineCode: string, groupCode: string): Promise<TeacherGradeRecord[]> =>
    apiClient.get<TeacherGradeRecord[]>(
      `/teacher/gradebook?discipline_code=${encodeURIComponent(disciplineCode)}&group_code=${encodeURIComponent(groupCode)}`
    ),

  saveGradebook: (payload: {
    discipline_code: string;
    group_code: string;
    grades: Array<{
      student_name: string;
      student_user_id?: string | null;
      week_number: number;
      lecture_score: number;
      practice_score: number;
      sro_score: number;
    }>;
  }): Promise<TeacherGradeRecord[]> =>
    apiClient.post<TeacherGradeRecord[]>('/teacher/gradebook', payload),

  getAttendance: (
    disciplineCode: string,
    groupCode: string,
    weekNumber?: number
  ): Promise<TeacherAttendanceRecord[]> => {
    const query = weekNumber
      ? `/teacher/attendance?discipline_code=${encodeURIComponent(disciplineCode)}&group_code=${encodeURIComponent(groupCode)}&week_number=${weekNumber}`
      : `/teacher/attendance?discipline_code=${encodeURIComponent(disciplineCode)}&group_code=${encodeURIComponent(groupCode)}`;
    return apiClient.get<TeacherAttendanceRecord[]>(query);
  },

  saveAttendance: (payload: {
    discipline_code: string;
    group_code: string;
    records: Array<{ student_user_id?: string | null; student_name: string; week_number: number; lesson_type: string; status: string }>;
  }): Promise<TeacherAttendanceRecord[]> =>
    apiClient.post<TeacherAttendanceRecord[]>('/teacher/attendance', payload),

  getScientificProjects: (): Promise<TeacherScientificProject[]> =>
    apiClient.get<TeacherScientificProject[]>('/teacher/scientific-projects'),

  createScientificProject: (payload: {
    title: string;
    direction?: string;
    stage_title?: string;
    stage_timeline?: string;
  }): Promise<TeacherScientificProject> =>
    apiClient.post<TeacherScientificProject>('/teacher/scientific-projects', payload),

  addProjectParticipant: (
    projectId: string,
    payload: {
      student_name: string;
      group_code: string;
      student_code?: string;
      program_name?: string;
      project_role?: string;
    }
  ): Promise<TeacherScientificProject> =>
    apiClient.post<TeacherScientificProject>(`/teacher/scientific-projects/${projectId}/participants`, payload),

  removeProjectParticipant: (projectId: string, studentName: string): Promise<TeacherScientificProject> =>
    apiClient.delete<TeacherScientificProject>(
      `/teacher/scientific-projects/${projectId}/participants/${encodeURIComponent(studentName)}`
    ),

  getRequests: (): Promise<TeacherStudentRequest[]> =>
    apiClient.get<TeacherStudentRequest[]>('/teacher/requests'),

  replyRequest: (
    requestId: string,
    payload: { reply_text: string; new_status?: string }
  ): Promise<TeacherStudentRequest> =>
    apiClient.put<TeacherStudentRequest>(`/teacher/requests/${requestId}/reply`, payload),

  getAttestation: (disciplineCode: string, groupCode: string): Promise<TeacherAttestationEntry[]> =>
    apiClient.get<TeacherAttestationEntry[]>(
      `/teacher/attestation?discipline_code=${encodeURIComponent(disciplineCode)}&group_code=${encodeURIComponent(groupCode)}`
    ),

  generateAttestation: (disciplineCode: string, groupCode: string): Promise<TeacherAttestationEntry[]> =>
    apiClient.post<TeacherAttestationEntry[]>('/teacher/attestation/generate', {
      discipline_code: disciplineCode,
      group_code: groupCode,
    }),

  getFiles: (category?: string): Promise<TeacherPersonalFile[]> => {
    const query =
      category && category !== 'Все'
        ? `/teacher/files?category=${encodeURIComponent(category)}`
        : '/teacher/files';
    return apiClient.get<TeacherPersonalFile[]>(query);
  },

  uploadFile: (payload: {
    category: string;
    title: string;
    filename: string;
    file_size_label?: string;
  }): Promise<TeacherPersonalFile> =>
    apiClient.post<TeacherPersonalFile>('/teacher/files', payload),

  getMeetings: (): Promise<TeacherMeeting[]> =>
    apiClient.get<TeacherMeeting[]>('/teacher/meetings'),

  createMeeting: (payload: {
    title: string;
    meeting_type: string;
    scheduled_at: string;
    location_or_url?: string;
    participants: string[];
  }): Promise<TeacherMeeting> =>
    apiClient.post<TeacherMeeting>('/teacher/meetings', payload),

  askSuperAI: (
    query: string,
    disciplineCode?: string
  ): Promise<{ answer: string; action_type?: string; suggested_steps: string[] }> =>
    apiClient.post<{ answer: string; action_type?: string; suggested_steps: string[] }>('/teacher/ai-assistant', {
      query,
      discipline_code: disciplineCode,
    }),
};
